import logger from '../common/logger'
import TrackingManager from '../guCommonTracking'
import AffiliateIdHttpClient from '../clients/affiliateIdHttpClient'
import {getAltDomain} from '../utils/urls'

const DEFAULT_AFF_ID = '11258'
const DEFAULT_NID = 373
const BACKUP_AFF_ID = '1112'

const EF_CLICK_MAX_RETRIES = 15
const EF_CLICK_RETRY_DELAY = 100

const EF_EVENT_MAX_RETRIES = 20
const EF_EVENT_RETRY_DELAY = 1000

const module = 'EverflowTracker'
const buildEfOrder = (order, isPostTransactionConversion) => {
  try {
    const efOrder = {
      advid: order.friendly_id || order.id,
      amt: order?.total,
      bs: order?.shipping_region,
      bc: order?.shipping_country,
      cc: order?.discount?.code,
      items: [],
    }

    if (!isPostTransactionConversion) {
      efOrder.oid = order?.id
    }

    efOrder.items = order.items.map((item) => {
      return {
        ps: `${item.mpn || item.manufacturer_product_number}`,
        p: item.price,
        qty: item.quantity,
      }
    })
    return efOrder
  } catch (e) {
    logger.error('buildEfOrder Failed', e, { module })
    return {}
  }
}

const generateCustomParameters = (additionalCustomParameters = {}) => {
  let parameters = additionalCustomParameters

  if (window.gu_id) {
    parameters.gu_uuid = window.gu_id
  }

  // Set parameters from window.gu_qs where key starts with utm_
  Object.keys(window.gu_qs)
    .filter((key) => key.startsWith('utm_'))
    .map((key) => (parameters[key] = window.gu_qs[key]))

  // Set page config parameters from URL
  const searchParams = new URLSearchParams(window.location.search)
  const pageConfigParams = ['pg', 'discount']

  for (const param of pageConfigParams) {
    if (searchParams.has(param)) {
      parameters[param] = searchParams.get(param)
    }
  }

  if (searchParams.has('altdomain')) {
    parameters.exclusive_domain = searchParams.get('altdomain')
  }

  return parameters
}

const EverflowEventMappings = {
  'Splash Page': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.vsl_landing_event,
  }),
  'Presell Page': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.presell_landing_event,
  }),
  'Listicle Landing': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.listicle_landing_event,
  }),
  'Landing Page': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.landing_page_event,
  }),
  'Custom Landing Page': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.custom_landing_page_event,
  }),
  'Splash Page Start': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.vsl_landing_start_event,
  }),
  'Presell Page Start': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.presell_landing_start_event,
  }),
  'Landing Page Start': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.landing_page_start_event,
  }),
  'Select Bundle': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.add_to_cart_event,
  }),
  'View Upsell': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.view_upsell,
    passCustomParameters: true,
  }),
  'View Pop Sale': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.view_popup_upsell,
  }),
  'Shipping Info': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.shipping_info_event,
  }),
  'Shipping Method': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.shipping_method_event,
  }),
  'Initiate Checkout': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.initiate_checkout_event,
  }),
  MobileSummaryOpen: () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.mobile_summary_open,
  }),
  'Start Checkout': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.start_checkout_event,
  }),
  'Order Conversion': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    conversionEvent: true,
  }),
  'Post Transaction Conversion 1': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.post_purchase_event_1,
    conversionEvent: true,
  }),
  'Post Transaction Conversion 2': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.post_purchase_event_2,
    conversionEvent: true,
  }),
  'Post Transaction Conversion 3': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.post_purchase_event_3,
    conversionEvent: true,
  }),
  'Abandoned Cart 1': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_1,
  }),
  'Abandoned Cart 2': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_2,
  }),
  'Abandoned Cart 3': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_3,
  }),
  'Abandoned Cart 4': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_4,
  }),
  'Abandoned Cart 5': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_5,
  }),
  'Abandoned Cart 6': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_6,
  }),
  'Abandoned Cart 7': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_7,
  }),
  'Abandoned Cart 8': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_8,
  }),
  'Abandoned Cart 9': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_9,
  }),
  'Abandoned Cart 10': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_10,
  }),
  'Abandoned Cart Conversion': () => ({
    advertiser_id: TrackingManager.config?.advertiser_id,
    event_id: TrackingManager.config?.abandoned_cart_conversion,
  }),
}

const setAffIdInUrl = (affId) => {
  const url = new URL(window.location.href)
  url.searchParams.set('aff_id', affId)
  url.searchParams.set('affid', affId)
  window.history.replaceState({}, '', url.toString())
}

const EverflowTracker = {
  retries: {
    click: 0,
    events: {},
  },
  trackClick: async () => {
    return new Promise((resolve) => {
      const ensureReqId = async function () {
        // Exit if visitor is not in ship-to country
        if (window.giddyup?.visitorInShippingCountry === false) {
          return
        }

        if (!window.gu_qs?.req_id || window.gu_qs?.req_id === '') {
          EverflowTracker.retries.click = 0

          // Generate req_id with backup account if empty
          window.gu_qs['aff_id'] = BACKUP_AFF_ID

          setAffIdInUrl(BACKUP_AFF_ID)

          efClick()
        }
      }

      const efClick = async function () {
        if (!window.gu_qs?.aff_id || window?.gu_qs?.aff_id === '') {
          let affId = DEFAULT_AFF_ID

          const domain =
            getAltDomain() ||
            TrackingManager.config?.domain ||
            window.location.host

          const affIdResult = await AffiliateIdHttpClient.getAffiliateIdAsync(
            domain
          )

          if (affIdResult?.data?.affiliateId) {
            affId = affIdResult.data.affiliateId
          }

          window.gu_qs.aff_id = affId

          setAffIdInUrl(affId)
        }

        const oid =
          window?.gu_qs?.oid && window?.gu_qs?.oid != ''
            ? window?.gu_qs?.oid
            : TrackingManager.config?.offer_id ?? null
        const affId = window.gu_qs.aff_id

        if (window.EF && oid && affId) {
          window.EF.click({
            offer_id: oid,
            affiliate_id: affId,
            uid: window?.gu_qs?.uid,
            adv2: TrackingManager.config?.adv2,
            sub1: window?.gu_qs?.sub1,
            sub2: window?.gu_qs?.sub2,
            sub3: window?.gu_qs?.sub3,
            sub4: window?.gu_qs?.sub4,
            sub5: window?.gu_qs?.sub5,
            source_id: window?.gu_qs?.source_id,
            parameters: generateCustomParameters(),
          }).then((req_id) => {
            EverflowTracker.retries.click = 0

            window.gu_qs['req_id'] = req_id

            const currentUrl = new URL(window.location.href)
            currentUrl.searchParams.set('req_id', req_id)
            window.history.replaceState({}, '', currentUrl.toString())

            resolve(req_id)
          })

          // Ensure a backup aff ID is used if we haven't yet generated a transaction_id
          if (window.gu_qs['aff_id'] !== BACKUP_AFF_ID) {
            setTimeout(ensureReqId, EF_CLICK_MAX_RETRIES * EF_CLICK_RETRY_DELAY)
          }
        } else if (
          oid &&
          affId &&
          EverflowTracker.retries.click < EF_CLICK_MAX_RETRIES
        ) {
          EverflowTracker.retries.click++
          setTimeout(efClick, EF_CLICK_RETRY_DELAY)
        } else {
          resolve('')
        }
      }
      efClick()
    })
  },
  trackEvent: (eventProperties) => {
    try {
      const { eventName, propertyMeta } = eventProperties
      const eventMapping = EverflowEventMappings[eventName]
      if (eventMapping && eventMapping().conversionEvent) {
        EverflowTracker.trackConversion(eventProperties)
      } else if (eventMapping) {
        const event = EverflowEventMappings[eventName]()
        if (event.event_id) {
          if (eventProperties?.adv2) {
            event.adv2 = eventProperties.adv2
          }
          EverflowTracker.track(event, event.passCustomParameters ? propertyMeta : {})
        }
      }
    } catch (e) {
      logger.error('trackEvent Failed', e, { module })
    }
  },
  trackConversion: (props) => {
    try {
      let { propertyMeta, eventName } = props
      const event = EverflowEventMappings[eventName]() || {}
      const order = propertyMeta
      if (order) {
        if (window.EF) {
          let adv5
          let percentDiscount = 0
          let freeShippingDiscount = false

          if (order?.discount_total) {
            // CHK-1758: [FE] [Discount Codes] Update ADV if discount code is used
            // Calculate actual discount and % Discount
            let actualDiscount = 0

            order?.transactions?.forEach((transaction) => {
              actualDiscount = parseFloat(transaction.discount_total) || 0.0
              transaction?.discounts?.forEach((discount) => {
                if (discount?.type === 'percent') {
                  percentDiscount =
                    (parseFloat(discount.amount) /
                      parseFloat(transaction.subtotal_without_discounts)) *
                    100
                } else if (discount?.type === 'free_shipping') {
                  freeShippingDiscount = true
                }
              })
            })

            adv5 = actualDiscount
          }

          const isPostTransactionConversion = eventName.includes(
            'Post Transaction Conversion'
          )

          let efconv = {
            aid: event.advertiser_id,
            amount: order.total,
            nid: DEFAULT_NID,
            adv3: order.offer,
            adv5: adv5 > 0 ? adv5 : null,
            order: buildEfOrder(order, isPostTransactionConversion),
          }

          // Don't include adv1 for post transaction conversions
          if (!isPostTransactionConversion) {
            efconv.adv1 = order.friendly_id || order.id
          }

          // If commission_id is provided, overwrite adv3 with its value
          if (order.commission_id != null) {
            efconv.adv3 = order.commission_id
          }

          if (percentDiscount > 0) {
            efconv.adv4 = Math.round(percentDiscount)
          } else if (freeShippingDiscount) {
            efconv.adv4 = 'FS'
          }

          // Pass some additional data if we're using the house affiliate ID
          if (window.gu_qs.aff_id === DEFAULT_AFF_ID) {
            efconv.adv2 = 'Credited Overage'
          }

          // If config has adv2, set it in efconv (for listicle slug)
          if (TrackingManager.config?.adv2) {
            efconv.adv2 = TrackingManager.config.adv2
          }

          if (event.event_id) {
            efconv.adv_event_id = event.event_id
          }
          if (window.gu_qs && window.gu_qs['req_id']) {
            efconv.transaction_id = window.gu_qs['req_id']
          }
          if (window.gu_qs && window.gu_qs['listicle_origin']) {
            efconv.adv5 = window.gu_qs['listicle_origin']
          }

          efconv.parameters = generateCustomParameters()

          window.EF.conversion(efconv)
            .then((conversion) => {
              logger.info('Everflow Conversion', {
                everflowConversionResponse: conversion,
              })
            })
            .catch((error) => {
              logger.error('Everflow Conversion error', {
                everflowConversionResponse: error,
              })
            })
        } else {
          let transactionId = ''
          if (window.gu_qs && window.gu_qs['req_id']) {
            transactionId += window.gu_qs['req_id']
          }

          let listicleOrigin = ''
          if (window.gu_qs && window.gu_qs['listicle_origin']) {
            listicleOrigin += window.gu_qs['listicle_origin']
          }

          let script = document.createElement('iframe')
          script.width = 1
          script.height = 1
          script.frameBorder = 0
          script.src =
            'https://www.gu-ecom.com/?nid=' +
            DEFAULT_NID +
            '&advid=' +
            TrackingManager.config.advertiser_id +
            '&transaction_id=' +
            transactionId +
            '&adv5=' +
            listicleOrigin
          document.getElementsByTagName('script')[0].appendChild(script)
        }
      }
    } catch (e) {
      logger.error('trackConversion Failed', e, { module })
    }
  },
  track: (event, additionalCustomParameters = {}) => {
    const advertiser = event.advertiser_id
    const adv_event = event.event_id

    if (!EverflowTracker.retries.events[event.event_id]) {
      EverflowTracker.retries.events[event.event_id] = 0
    }

    try {
      if (window.EF) {
        let efPixel = {
          aid: advertiser,
          adv_event_id: adv_event,
          nid: DEFAULT_NID,
        }
        if (window.gu_qs && window.gu_qs['req_id']) {
          efPixel.transaction_id = window.gu_qs['req_id']
        }

        if (event.adv2) {
          efPixel.adv2 = event.adv2
        }

        efPixel.parameters = generateCustomParameters(additionalCustomParameters)

        window.EF.conversion(efPixel)
          .then((conversion) => {
            logger.info('Everflow Event', {
              everflowConversionResponse: conversion,
            })
            if (
              !conversion['conversion_id'] &&
              EverflowTracker.retries.events[event.event_id] < EF_EVENT_MAX_RETRIES
            ) {
              EverflowTracker.retries.events[event.event_id]++
              setTimeout(() => {
                EverflowTracker.track(event, additionalCustomParameters)
              }, EF_EVENT_RETRY_DELAY)
            } else {
              EverflowTracker.retries.events[event.event_id] = 0
            }
          })
          .catch((error) => {
            logger.error('Everflow Event error', {
              everflowConversionResponse: error,
            })
          })
      } else {
        let transactionId = ''
        if (window.gu_qs && window.gu_qs['req_id']) {
          transactionId += window.gu_qs['req_id']
        }
        let script = document.createElement('iframe')
        script.width = 1
        script.height = 1
        script.src =
          'https://www.gu-ecom.com/?nid=' +
          DEFAULT_NID +
          '&advid=' +
          advertiser +
          '&adv_event_id=' +
          adv_event +
          '&transaction_id=' +
          transactionId
        document.getElementsByTagName('script')[0].appendChild(script)
      }
    } catch (e) {
      logger.error('track Failed', e, { module })
    }
  },
}
export default EverflowTracker
